import { injectable } from "inversify";

/**
 * Service for loading assets from authenticated endpoints.
 * Replaces Phaser's loader for backend API calls that require authentication cookies.
 */
@injectable()
export class AuthenticatedAssetLoader {
    /**
     * Load JSON data from an authenticated endpoint and add it to the scene's cache.
     * @param scene The Phaser scene
     * @param key The cache key for the loaded data
     * @param url The URL to fetch from
     * @returns Promise that resolves when the data is loaded and cached
     */
    async loadJson(scene: Phaser.Scene, key: string, url: string): Promise<any> {
        try {
            const response = await fetch(url, {
                method: "GET",
                credentials: "include", // Send cookies for authentication
                headers: {
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to load ${key}: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();

            // Add to Phaser's cache so it can be retrieved like normal loaded assets
            if (scene.cache.json.exists(key)) {
                scene.cache.json.remove(key);
            }
            scene.cache.json.add(key, data);

            console.log("successfully loaded json asset ", key, " from ", url);
            return data;
        } catch (error) {
            console.error(`Error loading JSON asset ${key} from ${url}:`, error);
            throw error;
        }
    }

    /**
     * Load multiple JSON assets in parallel.
     * @param scene The Phaser scene
     * @param assets Array of {key, url} objects
     * @returns Promise that resolves when all assets are loaded (or attempted)
     */
    async loadJsonBatch(
        scene: Phaser.Scene,
        assets: Array<{ key: string; url: string }>
    ): Promise<any[]> {
        const promises = assets.map((asset) => this.loadJson(scene, asset.key, asset.url));
        console.log("loading batch of authenticated json assets: ", assets);

        const results = await Promise.allSettled(promises);

        // Separate successes and failures
        const successes = results.filter((r) => r.status === "fulfilled");
        const failures = results.filter((r) => r.status === "rejected");

        // Log detailed error information for debugging
        if (failures.length > 0) {
            console.error(`Failed to load ${failures.length} out of ${assets.length} assets:`);
            failures.forEach((result, index) => {
                // Find which asset failed by matching the index
                const failedAssetIndex = results.indexOf(result);
                const failedAsset = assets[failedAssetIndex];
                console.error(`  - ${failedAsset.key} from ${failedAsset.url}:`, result);
            });
        }

        console.log(`Successfully loaded ${successes.length} out of ${assets.length} assets`);

        // Return the successfully loaded data
        return successes.map((r) => r);
    }

    /**
     * Fetch a URL with credentials and return a blob URL for use with Phaser's loader.
     * Caller is responsible for calling URL.revokeObjectURL() when done.
     */
    async fetchAsBlob(url: string): Promise<string> {
        const response = await fetch(url, {
            method: "GET",
            credentials: "include",
        });
        if (!response.ok) {
            throw new Error(
                `Failed to fetch image: ${response.status} ${response.statusText} (${url})`
            );
        }
        const blob = await response.blob();
        return URL.createObjectURL(blob);
    }

    /**
     * Load an image from an authenticated endpoint.
     * @param scene The Phaser scene
     * @param key The cache key for the loaded image
     * @param url The URL to fetch from
     * @returns Promise that resolves when the image is loaded
     */
    async loadImage(scene: Phaser.Scene, key: string, url: string): Promise<void> {
        return new Promise((resolve, reject) => {
            fetch(url, {
                method: "GET",
                credentials: "include",
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`Failed to load image ${key}: ${response.status}`);
                    }
                    return response.blob();
                })
                .then((blob) => {
                    const objectURL = URL.createObjectURL(blob);
                    const loader = scene.load.image(key, objectURL);
                    scene.load.once("complete", () => {
                        URL.revokeObjectURL(objectURL);
                        resolve();
                    });
                    scene.load.once("loaderror", () => {
                        URL.revokeObjectURL(objectURL);
                        reject(new Error(`Failed to load image ${key}`));
                    });
                    scene.load.start();
                })
                .catch((error) => {
                    console.error(`Error loading image ${key} from ${url}:`, error);
                    reject(error);
                });
        });
    }
}
